<template>
  <main>
    <!-- CREATE ARENA DIALOG -->
    <v-dialog v-model="showArenaForm" max-width="400">
      <v-card class="pa-4">
        <v-text-field
          class="px-2"
          v-model="newArenaName"
          placeholder="Arena name"
        />
        <v-card-actions>
          <v-btn @click="closeArenaForm">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createArena">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- CREATE RANGE DIALOG -->
    <v-dialog v-model="showRangeForm" max-width="400">
      <v-card class="pa-4">
        <v-text-field
          class="px-2"
          v-model="newRangeName"
          placeholder="Range name"
        />
        <v-card-actions>
          <v-btn @click="closeRangeForm">Cancel</v-btn>
          <v-spacer />
          <v-btn @click="createRange">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card-title>
      <div class="header">
        <span>Arenas</span>
        <v-btn color="secondary" @click="showArenaForm = true">
          Create Arena
        </v-btn>
        <v-text-field
          class="text-field"
          v-model="arenaSearch"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        />
      </div>
    </v-card-title>

    <!-- ARENA LIST -->
    <v-data-table
      :headers="headers"
      :search="arenaSearch"
      :items="arenas"
      :expanded.sync="expandedArenas"
      :loading="$apollo.queries.arenas.loading"
      hide-default-footer
      show-expand
      single-expand
      disable-pagination
      item-key="id"
    >
      <template v-slot:item.actions="{ item }">
        <v-btn icon @click="deleteArena(item.id)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <section class="pa-4 table-content">
            <div class="d-flex flex-row align-center">
              <v-text-field
                class="text-field mr-4"
                placeholder="Arena name"
                v-model="selectedArenaName"
              />
              <v-btn @click="updateArena(item.id, selectedArenaName)">
                Save
              </v-btn>
            </div>
            <div>
              <v-btn
                class="my-4"
                color="secondary"
                @click="showRangeForm = true"
              >
                Create new range
              </v-btn>
            </div>

            <!-- RANGE LIST -->
            <v-data-table
              :headers="headers"
              :items="selectedArenaRanges"
              :expanded.sync="expandedRanges"
              :loading="selectedArenaRangesLoading"
              hide-default-footer
              show-expand
              single-expand
              disable-pagination
              item-key="id"
            >
              <template v-slot:item.actions="{ item }">
                <v-btn icon @click="deleteRange(item.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <section class="pa-4 table-content">
                    <div class="d-flex flex-row align-center">
                      <v-text-field
                        class="text-field mr-4"
                        placeholder="Range name"
                        v-model="selectedRangeName"
                      />
                      <v-btn @click="updateRange(item.id, selectedRangeName)">
                        Save
                      </v-btn>
                    </div>
                  </section>
                </td>
              </template>
            </v-data-table>
          </section>
        </td>
      </template>
    </v-data-table>

    <Popup
      :show="showErrorMessage"
      :text="errorMessage"
      title="Error"
      type="error"
      @close="showErrorMessage = false"
    />
    <c-confirmation-dialog ref="confirmationDialog" />
  </main>
</template>

<script>
import {
  GET_ARENAS,
  GET_ARENA_WITH_RANGES,
  CREATE_ARENA,
  CREATE_RANGE,
  UPDATE_ARENA,
  UPDATE_RANGE,
  DELETE_ARENA,
  DELETE_RANGE,
} from '../../graphql/ownerManagementQueries';

export default {
  components: {
    CConfirmationDialog: () => import('../common/CConfirmationDialog'),
    Popup: () => import('../common/Popup'),
  },
  data() {
    return {
      arenaSearch: '',
      showArenaForm: false,
      showRangeForm: false,

      selectedArenaRangesLoading: false,
      showErrorMessage: false,
      errorMessage: '',

      newArenaName: '',
      newRangeName: '',
      selectedArenaName: '',
      selectedRangeName: '',

      arenas: [],
      selectedArenaRanges: [],

      // Tables
      expandedArenas: [],
      expandedRanges: [],
      headers: [
        { text: '', value: 'data-table-expand' },
        { text: 'Name', value: 'name', align: 'left', sortable: false },
        { text: 'ID', value: 'id', align: 'left', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false,
        },
      ],
    };
  },
  apollo: {
    $client: 'ownerManagementClient',
    arenas: {
      query: GET_ARENAS,
      update: (data) => data.getArenas,
    },
  },
  mounted() {
    this.$apollo.queries.arenas.refetch();
  },
  watch: {
    async expandedArenas(expandedArenas) {
      this.expandedRanges = [];

      if (expandedArenas.length === 0) {
        this.selectedArenaRanges = [];
        this.selectedArenaName = '';
        return;
      }

      // Get ranges and set editable name for when a new arena is expanded
      this.selectedArenaName = expandedArenas[0].name;
      await this.refreshSelectedArenaRanges();
    },
    async expandedRanges(expandedRanges) {
      if (expandedRanges.length === 0) {
        this.selectedRangeName = '';
        return;
      }

      // Set editable name for when a new range is expanded
      this.selectedRangeName = expandedRanges[0].name;
    },
  },
  methods: {
    async refreshSelectedArenaRanges() {
      this.selectedArenaRangesLoading = true;
      this.selectedArenaRanges = [];
      const arena = await this.getArenaWithRanges(this.expandedArenas[0].id);
      if (!arena) {
        this.selectedArenaRangesLoading = false;
        this.displayError('Failed to get arena with ranges');
        return;
      }

      this.selectedArenaRanges = arena.ranges;
      this.selectedArenaRangesLoading = false;
    },
    async getArenaWithRanges(id) {
      try {
        const res = await this.$apollo.query({
          query: GET_ARENA_WITH_RANGES,
          fetchPolicy: 'no-cache',
          variables: {
            id,
          },
        });

        if (!res.data.getArena) {
          this.displayError('Failed to get arena with ranges');
          return undefined;
        }

        return res.data.getArena;
      } catch (err) {
        this.displayError('Failed to get arena with ranges: ' + err);
        console.error(err);
        return undefined;
      }
    },
    async createArena() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_ARENA,
          variables: {
            name: this.newArenaName || 'New arena',
            ownerId: this.expandedOwners[0].id,
          },
        });

        if (!res.data.createArena || !res.data.createArena.id) {
          this.displayError('Arena creation failed');
          return;
        }

        await this.$apollo.queries.arenas.refetch();
      } catch (err) {
        this.displayError('Arena creation failed: ' + err);
        console.error(err);
      }

      this.closeArenaForm();
    },
    async createRange() {
      try {
        const res = await this.$apollo.mutate({
          mutation: CREATE_RANGE,
          variables: {
            name: this.newRangeName || 'New range',
            arenaId: this.expandedArenas[0].id,
          },
        });

        if (!res.data.createRange || !res.data.createRange.id) {
          this.displayError('Range creation failed');
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Range creation failed: ' + err);
        console.error(err);
      }

      this.closeRangeForm();
    },
    async updateArena(id, name) {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_ARENA,
          variables: {
            id,
            name,
          },
        });

        if (res.data.updateArena.result !== 'OK') {
          this.displayError(res.data.updateArena.details);
          return;
        }

        await this.$apollo.queries.arenas.refetch();
      } catch (err) {
        this.displayError('Arena update failed: ' + err);
        console.error(err);
      }
    },
    async updateRange(id, name) {
      try {
        const res = await this.$apollo.mutate({
          mutation: UPDATE_RANGE,
          variables: {
            id,
            name,
            arenaId: this.expandedArenas[0].id,
          },
        });

        if (res.data.updateRange.result !== 'OK') {
          this.displayError(res.data.updateRange.details);
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Range update failed: ' + err);
        console.error(err);
      }
    },
    async deleteArena(id) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this arena?'
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_ARENA,
          variables: {
            id: id,
          },
        });

        if (res.data.deleteArena.result !== 'OK') {
          this.displayError(res.data.deleteArena.details);
          return;
        }

        this.$apollo.queries.arenas.refetch();
      } catch (err) {
        this.displayError('Arena deletion failed: ' + err);
        console.error(err);
      }
    },
    async deleteRange(id) {
      if (
        !(await this.$refs.confirmationDialog.open(
          'Are you sure you want to delete this range? It will be deleted for every owner connected to this arena.'
        ))
      )
        return;

      try {
        const res = await this.$apollo.mutate({
          mutation: DELETE_RANGE,
          variables: {
            id,
          },
        });

        if (res.data.deleteRange.result !== 'OK') {
          this.displayError(res.data.deleteRange.details);
          return;
        }

        await this.refreshSelectedArenaRanges();
      } catch (err) {
        this.displayError('Range deletion failed: ' + err);
        console.error(err);
      }
    },
    displayError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
    },
    closeArenaForm() {
      this.showArenaForm = false;
      this.newArenaName = '';
    },
    closeRangeForm() {
      this.showRangeForm = false;
      this.newRangeName = '';
    },
  },
};
</script>

<style scoped>
.header {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.table-content {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.text-field {
  max-width: 300px;
}
</style>
